import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Video, CalendarDays, ArrowLeft, ArrowRight, PlugZap, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, fmtRel } from "@/lib/api";
import { PageHeader, Pill, Dot, EmptyState } from "@/components/app/shared";
import TasksWidget from "@/components/google/TasksWidget";
import { cn } from "@/lib/utils";

const DAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function startOfWeek(d) {
  const x = new Date(d);
  const diff = x.getDay(); // 0 = sunday — we use monday start
  const monday = new Date(x);
  monday.setDate(x.getDate() - ((diff + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function formatHour(iso, allDay) {
  if (allDay) return "todo el día";
  const d = new Date(iso);
  return d.toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" });
}

const HUB_KIND_LABEL = {
  story: "Publicación",
  "deal-deadline": "Deadline de pauta",
  "deal-run": "Salida de pauta",
  book: "Libro Anual",
};

export default function Agenda() {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const end = useMemo(() => { const e = new Date(anchor); e.setDate(anchor.getDate() + 7); return e; }, [anchor]);

  const { data, isLoading } = useQuery({
    queryKey: ["calendar-events", anchor.toISOString(), end.toISOString()],
    queryFn: async () => (await api.get(`/calendar/events?start=${anchor.toISOString()}&end=${end.toISOString()}`)).data,
    refetchInterval: 30_000,
  });

  const days = useMemo(() => {
    const list = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      list.push(d);
    }
    return list;
  }, [anchor]);

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[d.toISOString().slice(0, 10)] = []; });
    (data?.google || []).forEach((ev) => {
      const key = ev.start.slice(0, 10);
      if (map[key]) map[key].push({ ...ev, source: "google" });
    });
    (data?.hub || []).forEach((ev) => {
      const key = ev.start.slice(0, 10);
      if (map[key]) map[key].push({ ...ev });
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.allDay && !b.allDay ? -1 : !a.allDay && b.allDay ? 1 : a.start.localeCompare(b.start))));
    return map;
  }, [data, days]);

  const titleRange = `${days[0].getDate()} ${MONTHS[days[0].getMonth()]} – ${days[6].getDate()} ${MONTHS[days[6].getMonth()]} ${days[6].getFullYear()}`;

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="08 · Agenda editorial & reuniones"
        title="Agenda"
        subtitle="Reuniones de Calendar, deadlines del Hub y salidas de pauta — todo en una misma vista semanal."
        actions={<>
          <Button variant="outline" size="icon" onClick={() => { const d = new Date(anchor); d.setDate(anchor.getDate() - 7); setAnchor(d); }} aria-label="Semana anterior" data-testid="agenda-prev-week"><ArrowLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setAnchor(startOfWeek(new Date()))} data-testid="agenda-today">Esta semana</Button>
          <Button variant="outline" size="icon" onClick={() => { const d = new Date(anchor); d.setDate(anchor.getDate() + 7); setAnchor(d); }} aria-label="Semana siguiente" data-testid="agenda-next-week"><ArrowRight className="h-4 w-4" /></Button>
        </>}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="kicker">{titleRange}</div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={data?.googleConnected ? "mint" : "neutral"} data-testid="agenda-google-status">
            <Dot tone={data?.googleConnected ? "ok" : "muted"} />
            {data?.googleConnected ? `Google conectado${data?.syncedAt ? " · " + fmtRel(data.syncedAt) : ""}` : "Google sin conectar"}
          </Pill>
          {!data?.googleConnected && (
            <Button variant="outline" asChild data-testid="agenda-connect-link">
              <Link to="/integraciones"><PlugZap className="mr-1.5 h-4 w-4" /> Conectar Google</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <section className="rounded-lg border border-border bg-card" data-testid="agenda-week">
          {isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando agenda…</div>
          ) : (
            <ul className="divide-y divide-border">
              {days.map((d) => {
                const key = d.toISOString().slice(0, 10);
                const list = byDay[key] || [];
                const isToday = key === new Date().toISOString().slice(0, 10);
                return (
                  <li key={key} className={cn("grid gap-4 px-5 py-4 md:grid-cols-[120px_1fr]", isToday && "bg-butter/40")} data-testid="agenda-day">
                    <div>
                      <div className="font-display text-4xl leading-none">{d.getDate()}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{DAYS[d.getDay()]}</div>
                      {isToday && <Pill tone="lilac" className="mt-2 text-[10px]">hoy</Pill>}
                    </div>
                    <div>
                      {list.length === 0 ? (
                        <div className="text-[12.5px] italic text-muted-foreground">Sin eventos</div>
                      ) : (
                        <ul className="space-y-2">
                          {list.map((ev) => (
                            <li key={ev.key || ev.eventId} className="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2" data-testid="agenda-event">
                              <div className="w-16 shrink-0 font-mono text-[11px] text-muted-foreground">
                                {formatHour(ev.start, ev.allDay)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-medium leading-tight">{ev.summary}</div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                  {ev.source === "google" ? (
                                    <Pill tone="sky" className="text-[10px]"><Dot tone="ok" /> Google Calendar</Pill>
                                  ) : (
                                    <Pill tone="lilac" className="text-[10px]">Mediana Hub · {HUB_KIND_LABEL[ev.kind] || ev.kind}</Pill>
                                  )}
                                  {ev.location && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{ev.location}</span>}
                                </div>
                              </div>
                              <div className="flex shrink-0 gap-1.5">
                                {ev.hangoutLink && (
                                  <Button variant="outline" size="sm" asChild data-testid="agenda-meet-link">
                                    <a href={ev.hangoutLink} target="_blank" rel="noreferrer"><Video className="mr-1 h-3.5 w-3.5" /> Meet</a>
                                  </Button>
                                )}
                                {ev.htmlLink && (
                                  <Button variant="ghost" size="sm" asChild data-testid="agenda-open-link">
                                    <a href={ev.htmlLink} target="_blank" rel="noreferrer">Abrir</a>
                                  </Button>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {!isLoading && !data?.googleConnected && (
            <div className="border-t border-border px-5 py-3 text-[12px] text-muted-foreground" data-testid="agenda-google-hint">
              Conecta tu Google en Integraciones para ver tus reuniones de Calendar, no solo las fechas publicadas en Mediana Hub.
            </div>
          )}
          {data?.error && (
            <div className="border-t border-border bg-blush px-5 py-3 text-[12px] text-ink" data-testid="agenda-error">Aviso: {data.error}</div>
          )}
        </section>
        <TasksWidget title="Mis tareas" />
      </div>
    </div>
  );
}
